import { useState, type ReactNode } from 'react';
import { Icon } from '@/components/ui/Icon';
import { useMaxWidth, BP } from '@/shared/hooks/useMediaQuery';

/**
 * Master/detail two-pane (list rail | content). Above the narrow breakpoint
 * both show side by side; below it, it becomes single-column: the list, or —
 * once an item is selected — the detail with a back affordance. Used by
 * Sms / Chat so the two-pane doesn't crush on a phone/narrow split.
 */
export function TwoPane({
  list,
  detail,
  hasSelection,
  onBack,
  railWidth = 300,
  backLabel = 'חזרה לרשימה',
  height = 'calc(100vh - var(--topbar-h) - var(--gap) * 2)',
}: {
  list: ReactNode;
  detail: ReactNode;
  hasSelection: boolean;
  onBack: () => void;
  railWidth?: number;
  backLabel?: string;
  height?: string;
}) {
  const narrow = useMaxWidth(BP.narrow);
  const [, setTick] = useState(0); // re-render hook anchor (keeps API stable)

  if (narrow) {
    if (!hasSelection) {
      return <div style={{ height, padding: 'var(--gap)' }}>{list}</div>;
    }
    return (
      <div style={{ height, padding: 'var(--gap)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <button
          type="button"
          className="btn ghost"
          onClick={() => { onBack(); setTick((t) => t + 1); }}
          style={{ alignSelf: 'flex-start', marginBottom: 8 }}
        >
          <Icon name="chevron" size={14} /> {backLabel}
        </button>
        <div style={{ flex: 1, minHeight: 0 }}>{detail}</div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `${railWidth}px 1fr`,
        gap: 'var(--gap)',
        height,
        padding: 'var(--gap)',
      }}
    >
      {list}
      {detail}
    </div>
  );
}
